import React, { createContext, useContext, useMemo } from 'react';
import { useTemplateContext } from '../hooks/useTemplateContext';
import { createTemplateState } from '../state/TemplateManagementState';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * Template State Context
 *
 * Provides the appropriate template management state (Platform/Club/Branch)
 * to all child components based on the current route context.
 */
const TemplateStateContext = createContext(null);

/**
 * Hook to access the current template management state
 * @returns {Object} Template state and context
 * @throws {Error} If used outside of TemplateStateProvider
 */
export const useTemplateState = () => {
  const context = useContext(TemplateStateContext);
  if (!context) {
    throw new Error('useTemplateState must be used within a TemplateStateProvider');
  }
  return context;
};

/**
 * Provider component that injects the appropriate template management state
 * based on the current route (platform/club/branch)
 */
export const TemplateStateProvider = ({ children }) => {
  const templateContext = useTemplateContext();
  const { club_id } = useTenant();
  const { user } = useAuth();

  const value = useMemo(() => {
    const state = createTemplateState(templateContext);

    // Use tenant's club_id if available (from domain), otherwise fallback to user's club_id
    // This handles the case where club admins access via localhost without a subdomain
    const effectiveClubId = club_id || user?.club_id || null;

    console.log('[TemplateStateContext] Creating context value:', {
      isCoach: templateContext.isCoach,
      branchId: templateContext.branchId,
      user_location_id: user?.location_id,
      user_branch_id: user?.branch_id,
      contextType: templateContext.contextType
    });

    return {
      // State instance
      state,

      // Context information (for convenience)
      context: templateContext,

      // User context
      clubId: effectiveClubId,
      branchId: templateContext.branchId,

      // Commonly used flags
      isPlatformAdmin: templateContext.isPlatformAdmin,
      isClubAdmin: templateContext.isClubAdmin,
      isBranchAdmin: templateContext.isBranchAdmin,
      isCoach: templateContext.isCoach || false
    };
  }, [templateContext, club_id, user]);

  return (
    <TemplateStateContext.Provider value={value}>
      {children}
    </TemplateStateContext.Provider>
  );
};
